import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import {
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Icon,
  Divider,
} from '@mui/material';

import { Trans, useTranslation } from 'react-i18next';
import { Formik, Field, Form } from 'formik';
import { Add } from '@mui/icons-material';
import Autocomplete, { AutocompleteInputChangeReason } from '@mui/material/Autocomplete';

import { Input } from 'src/components/Input';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Typography } from 'src/components/Typography';
import { FormInputField } from 'src/components/FormInputField/FormInputField';
import { FormTextArea } from 'src/components/FormInputField/FormTextArea';
import { creatJobSchema } from './validationSchemas';
import { CreateRoleForm } from './CreateRoleForm.types';
import { useStoreState } from 'src/stores';
import { Role } from 'src/DTO/Role.type';
import { FeorNumber } from 'src/DTO/FeorNumber.type';
import { useDebounce } from 'src/utils/useDebounce';
import { useSearchFeorNumber } from 'src/api/hooks/useJob';
import { TYPOGRAPHY_TRANSFORM, TYPOGRAPHY_WEIGHT } from 'src/components/Typography/types';
import { BUTTON_COLOR, BUTTON_VARIANT } from 'src/components/Button/types';
import { LangContext } from 'src/context/LanguageContext';
import { EditModes } from '../Job.types';
import { NewJobData } from 'src/api/hooks/useJob.types';
import { useLocalizedData } from 'src/utils/useLocalizeData';
import { LanguageSelectorContext } from 'src/components/LanguageSelectorContext';
import { DELLA_COUNTIES } from 'src/utils/constants.util';
import { MobilePreviewData } from './MobilePreview';

const INIT_ROLE_DATA = {
  name: '',
  description: '',
  location: '',
  companyProfile: '',
  feor: undefined,
  postCode: '',
  city: '',
  address: '',
  descriptionEn: '',
  companyProfileEn: '',
  nameEn: '',
  county: '',
};

export interface CreateRoleProps {
  onCancel?: () => void;
  onSubmit: (roleData: CreateRoleForm) => void;
  roleId?: string;
  newRoleData?: CreateRoleForm | Role;
  editMode?: EditModes;
  newJobData?: NewJobData;
  setMobilePreviewData: React.Dispatch<React.SetStateAction<MobilePreviewData>>;
}

export const CreateRole = ({
  onCancel,
  onSubmit,
  roleId,
  newRoleData,
  editMode,
  newJobData,
  setMobilePreviewData,
}: CreateRoleProps) => {
  const { t } = useTranslation();

  const [searchFeor, { data: feorData, loading: feorLoading }] = useSearchFeorNumber();
  const { localizedRole, enJob, huJob, isEngOnly, shouldLocalize } = useLocalizedData(
    newJobData,
    editMode,
  );

  const feorCodeFromImport = newRoleData?.feor?.code;

  const { selectedLanguages } = useContext(LangContext);

  const roles = useStoreState(state => state.jobs.roles) as Role[];

  const [selectedRole, setSelectedRole] = useState<Role | undefined>(undefined);
  const [open, setOpen] = useState(false);

  const [feorSearch, setFeorSearch] = useState('');
  const [selectedFeor, setSelectedFeor] = useState<FeorNumber | undefined>(undefined);
  const [feorError, setFeorError] = useState<boolean>(false);

  const nameInputRef = useRef<HTMLInputElement>(null);
  const debouncedSearchTerm = useDebounce(feorSearch, 500);

  const feorOptions: FeorNumber[] = useMemo(() => {
    if (!feorData) return [];
    return feorData.map(feor => ({ ...feor, label: `${feor.code}. ${feor.text}` }));
  }, [feorData]);

  const filteredRoles = useMemo(() => {
    return roles.filter(role => {
      const en = role?.localizedData?.en?.selectedLanguages?.en;
      const hu = role?.localizedData?.en?.selectedLanguages?.hu;
      const job = localizedRole?.selectedLanguages;

      if (editMode) {
        if (!job) {
          return !role.localizedData?.en;
        } else if (job.en && job.hu) {
          return hu && en;
        } else if (job.en && !job.hu) {
          return !hu && en;
        }
      }

      if (selectedLanguages.en && !selectedLanguages.hu) {
        return en && !hu;
      } else if (selectedLanguages.hu && selectedLanguages.en) {
        return en && hu;
      } else if (selectedLanguages.hu && !selectedLanguages.en) {
        return !role.localizedData?.en;
      }
    });
  }, [roles, selectedLanguages, editMode, newJobData]);

  const isFeorNumber = (value: any): value is FeorNumber => {
    return (
      value &&
      typeof value === 'object' &&
      'code' in value &&
      'text' in value &&
      typeof value.code === 'number' &&
      typeof value.text === 'string'
    );
  };

  const handleInputChange = (
    _event: React.SyntheticEvent<Element, Event>,
    value: string,
    reason?: AutocompleteInputChangeReason,
  ) => {
    if (reason === 'input') {
      setFeorSearch(value);
    }
  };

  useEffect(() => {
    if (feorCodeFromImport && feorData) {
      const foundFeor = feorData.find(feor => feor.code === Number(feorCodeFromImport));
      if (foundFeor) {
        setSelectedFeor({
          ...foundFeor,
          label: `${foundFeor.code}. ${foundFeor.text}`,
        });
      }
    }
  }, [feorCodeFromImport, feorData]);

  const handleOnChange = (value: string | FeorNumber | null) => {
    isFeorNumber(value) ? setSelectedFeor(value) : setSelectedFeor(undefined);
  };

  useEffect(() => {
    if (!selectedFeor) {
      searchFeor({ variables: { feorSearch: '' } });
    }
  }, [selectedFeor]);

  useEffect(() => {
    if (
      newRoleData?.feor &&
      selectedFeor?.label !== `${newRoleData.feor.code}. ${newRoleData.feor.text}`
    ) {
      setSelectedFeor({
        ...newRoleData.feor,
        label: `${newRoleData.feor.code}. ${newRoleData.feor.text}`,
      });
    }
  }, [newRoleData]);

  useEffect(() => {
    debouncedSearchTerm
      ? searchFeor({ variables: { feorSearch: String(debouncedSearchTerm) } })
      : searchFeor({ variables: { feorSearch: '' } });
  }, [debouncedSearchTerm]);

  const getInitialRoleData = useCallback((): CreateRoleForm => {
    const role = filteredRoles.find(role => role.id === roleId);

    if (!role) {
      return newRoleData ? { ...newRoleData } : { ...INIT_ROLE_DATA };
    }

    if (!selectedRole && !!editMode) setSelectedRole(role);

    return {
      ...role,
      postCode: role.postCode || '',
      city: role.city || '',
      address: role.address || '',
      descriptionEn: role.localizedData?.en?.description || '',
      companyProfileEn: role.localizedData?.en?.companyProfile || '',
      nameEn: role.localizedData?.en?.name || '',
      county: role?.county || '',
    };
  }, [roleId, filteredRoles, selectedRole, newRoleData, editMode]);

  const selectRole = useCallback(
    (
      selectedRoleId: string,
      setValues: (roleData: CreateRoleForm, shouldValidate: boolean) => void,
    ) => {
      const role = filteredRoles.find(role => role.id === selectedRoleId);

      if (!role) {
        return;
      }

      if (role.feor) {
        setSelectedFeor({
          ...role.feor,
          label: `${role.feor.code}. ${role.feor.text}`,
        });
      } else {
        setSelectedFeor(undefined);
      }

      const { id, ...selectedRoleDataWithoutId } = {
        ...role,
        nameEn: role.localizedData?.en?.name || '',
        descriptionEn: role.localizedData?.en?.description || '',
        companyProfileEn: role.localizedData?.en?.companyProfile || '',
        postCode: role.postCode || '',
        city: role.city || '',
        address: role.address || '',
      };

      setMobilePreviewData(prev => ({
        ...prev,
        name: role.name,
        city: role.city,
        companyProfile: role.companyProfile,
        description: role.description,
        localizedData: {
          ...prev.localizedData,
          name: role?.localizedData?.en?.name,
          description: role.localizedData?.en?.description,
          companyProfile: role.localizedData?.en?.companyProfile,
        },
      }));
      setSelectedRole({ id, ...selectedRoleDataWithoutId });
      setValues(selectedRoleDataWithoutId, true);
    },
    [filteredRoles],
  );

  const addFocusToNameInput = () => {
    requestAnimationFrame(() => {
      if (!nameInputRef.current) return;
      nameInputRef.current.focus();
    });
  };

  useEffect(() => {
    addFocusToNameInput();
  }, []);

  const resetForm = useCallback((setValues: (roleData: CreateRoleForm) => void) => {
    setValues({ ...INIT_ROLE_DATA });
    setSelectedRole(undefined);
    setSelectedFeor(undefined);
    setFeorSearch('');
    setMobilePreviewData(prev => ({
      ...prev,
      name: '',
      city: '',
      companyProfile: '',
      description: '',
      localizedData: {
        ...prev.localizedData,
        name: '',
        description: '',
        companyProfile: '',
      },
    }));
    addFocusToNameInput();
  }, []);

  const displayRoleName = (role: Role | undefined): string => {
    if (!role) return '';

    if (isEngOnly) return role.localizedData?.en?.name ?? '';

    return role.name ?? '';
  };

  const handleSubmit = useCallback(
    async (data: CreateRoleForm, validateForm: () => Promise<any>) => {
      setFeorError(false);
      try {
        const errors = await validateForm();
        const localizedData = {
          selectedLanguages: !editMode ? selectedLanguages : localizedRole?.selectedLanguages,
          description: data.descriptionEn,
          companyProfile: data.companyProfileEn,
          name: data.nameEn,
        };

        if (!!Object.keys(errors).length) return;

        const submitData = {
          ...data,
          ...(!!editMode && { id: roleId }),
          feor: selectedFeor,
          ...(shouldLocalize && { localizedData }),
        };

        onSubmit(submitData);
      } catch (error) {
        console.log(error);
      }
    },
    [
      onSubmit,
      selectedFeor,
      selectedLanguages,
      selectedRole,
      editMode,
      localizedRole,
      shouldLocalize,
    ],
  );

  const focusFieldName = useMemo(() => {
    if (!!editMode) {
      return undefined;
    }

    if (!editMode) {
      return selectedLanguages.hu ? 'name' : 'nameEn';
    }

    if (!localizedRole || huJob) {
      return 'name';
    }
    if (isEngOnly && enJob) {
      return 'nameEn';
    }

    return 'name';
  }, [editMode, selectedLanguages, localizedRole, huJob, isEngOnly, enJob]);

  return (
    <Box>
      <Box width="80%" textAlign="center" mx="auto" mb={4}>
        <Typography variant="h5" fontWeight={TYPOGRAPHY_WEIGHT.REGULAR}>
          {!filteredRoles.length || !editMode ? (
            <Trans>jobPage.createJobModal.create</Trans>
          ) : (
            <Trans>jobPage.createJobModal.chooseOrCreate</Trans>
          )}
        </Typography>
      </Box>
      <Formik
        initialValues={getInitialRoleData()}
        validateOnChange={false}
        validationSchema={creatJobSchema(t)}
        onSubmit={() => {
          console.log('submit');
        }}>
        {({
          setValues,
          values,
          touched,
          validateForm,
          errors,
          setFieldTouched,
          isSubmitting,
          setFieldValue,
          handleChange,
        }) => {
          return (
            <Grid container spacing={4}>
              <Grid item sm={12} md={6}>
                <Form>
                  <Box>
                    {!editMode && (
                      <LanguageSelectorContext
                        onClick={() => resetForm(setValues)}></LanguageSelectorContext>
                    )}
                  </Box>
                  {!!filteredRoles.length && !editMode && (
                    <Box mb={5}>
                      <FormControl variant="filled" sx={{ width: '100%' }}>
                        <Select
                          displayEmpty
                          input={
                            <Input
                              inputLabel={t('jobPage.createJobForm.selectRoleTemplate')}
                              icon={{
                                component: <Icon>expand_more</Icon>,
                                direction: 'right',
                              }}
                            />
                          }
                          renderValue={selected => {
                            if (!selected) {
                              return (
                                <Box display="flex" alignItems="center">
                                  <Add color="primary" sx={{ marginRight: 1 }} />
                                  {t('jobPage.createJobForm.newRole')}
                                </Box>
                              );
                            }
                            return displayRoleName(selectedRole);
                          }}
                          value={selectedRole ? selectedRole.id : ''}>
                          <MenuItem onClick={() => resetForm(setValues)}>
                            <Box display="flex" alignItems="center">
                              <Add color="primary" sx={{ marginRight: 1 }} />
                              <Trans>generic.add</Trans>
                              <Divider sx={{ marginBottom: 1, color: 'divider' }} />
                            </Box>
                          </MenuItem>
                          <Divider />
                          {filteredRoles.map(role => (
                            <MenuItem
                              key={role.id}
                              value={role.id}
                              onClick={() => selectRole(role.id, setValues)}>
                              {displayRoleName(role)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                  {!editMode ? (
                    <>
                      {selectedLanguages.hu && (
                        <Field
                          label={t('jobPage.createRoleForm.jobTitle')}
                          name="name"
                          component={FormInputField}
                          variant="outlined"
                          error={touched.name || isSubmitting ? errors.name : undefined}
                          helperText={errors.name}
                          inputRef={focusFieldName === 'name' ? nameInputRef : undefined}
                          onChange={(e: React.FocusEvent<any>) => {
                            handleChange(e);
                            setMobilePreviewData(prev => ({ ...prev, name: e.target.value }));
                          }}
                        />
                      )}
                      {selectedLanguages.en && (
                        <Field
                          label={t('jobPage.createRoleForm.jobTitleEn')}
                          name="nameEn"
                          component={FormInputField}
                          variant="outlined"
                          error={touched.nameEn || isSubmitting ? errors.nameEn : undefined}
                          helperText={errors.nameEn}
                          inputRef={focusFieldName === 'nameEn' ? nameInputRef : undefined}
                          onChange={(e: React.FocusEvent<any>) => {
                            handleChange(e);
                            setMobilePreviewData(prev => ({
                              ...prev,
                              localizedData: { ...prev.localizedData, name: e.target.value },
                            }));
                          }}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {(!localizedRole || huJob) && (
                        <Field
                          label={t('jobPage.createRoleForm.jobTitle')}
                          name="name"
                          component={FormInputField}
                          variant="outlined"
                          error={touched.name || isSubmitting ? errors.name : undefined}
                          helperText={errors.name}
                          inputRef={focusFieldName === 'name' ? nameInputRef : undefined}
                          onChange={(e: React.FocusEvent<any>) => {
                            handleChange(e);
                            setMobilePreviewData(prev => ({ ...prev, name: e.target.value }));
                          }}
                        />
                      )}
                      {enJob && (
                        <Field
                          label={t('jobPage.createRoleForm.jobTitleEn')}
                          name="nameEn"
                          component={FormInputField}
                          variant="outlined"
                          error={touched.nameEn || isSubmitting ? errors.nameEn : undefined}
                          helperText={errors.nameEn}
                          inputRef={focusFieldName === 'nameEn' ? nameInputRef : undefined}
                          onChange={(e: React.FocusEvent<any>) => {
                            handleChange(e);
                            setMobilePreviewData(prev => ({
                              ...prev,
                              localizedData: { ...prev.localizedData, name: e.target.value },
                            }));
                          }}
                        />
                      )}
                    </>
                  )}
                  <Box mt={2} mb={4}>
                    <Box mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <Typography
                        component="label"
                        variant="caption"
                        fontWeight={TYPOGRAPHY_WEIGHT.BOLD}
                        textTransform={TYPOGRAPHY_TRANSFORM.CAPITALIZE}>
                        FEOR
                      </Typography>
                    </Box>
                    <Autocomplete
                      open={open}
                      onOpen={() => {
                        setOpen(true);
                      }}
                      onClose={() => {
                        setOpen(false);
                      }}
                      onChange={(_e, value) => handleOnChange(value)}
                      onInputChange={handleInputChange}
                      options={feorOptions}
                      getOptionLabel={option =>
                        typeof option === 'string'
                          ? option
                          : option.label || `${option.code}. ${option.text}`
                      }
                      isOptionEqualToValue={(option, value) => option.code === value.code}
                      loading={feorLoading}
                      freeSolo={true}
                      forcePopupIcon={true}
                      disableClearable={!selectedFeor}
                      value={selectedFeor || null}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={feorError}
                          helperText={feorError && t('validation.required')}
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <React.Fragment>
                                {feorLoading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Box>
                  <Box mt={2} mb={4}>
                    <Box mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <Typography variant="caption" fontWeight={TYPOGRAPHY_WEIGHT.BOLD}>
                        {t('jobPage.createRoleForm.county')}
                      </Typography>
                    </Box>
                    <Autocomplete
                      value={values.county || null}
                      onChange={(_, value) => setFieldValue('county', value || '', true)}
                      options={DELLA_COUNTIES}
                      renderInput={params => (
                        <TextField
                          {...params}
                          helperText={touched.county || isSubmitting ? errors.county : undefined}
                          error={!!errors.county}
                        />
                      )}
                    />
                  </Box>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Field
                        label={t('generic.postCode')}
                        name="postCode"
                        component={FormInputField}
                        variant="outlined"
                        error={touched.postCode || isSubmitting ? errors.postCode : undefined}
                        helperText={errors.postCode}
                        inputProps={{ maxLength: 4 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        label={t('generic.city')}
                        name="city"
                        component={FormInputField}
                        variant="outlined"
                        error={touched.city || isSubmitting ? errors.city : undefined}
                        helperText={errors.city}
                        onChange={(e: React.FocusEvent<any>) => {
                          handleChange(e);
                          setMobilePreviewData(prev => ({ ...prev, city: e.target.value }));
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Field
                        label={`${t('generic.street')}/${t('generic.streetNumber')}`}
                        name="address"
                        component={FormInputField}
                        variant="outlined"
                        error={
                          touched.address && errors.address && !isSubmitting
                            ? errors.address
                            : undefined
                        }
                        helperText={errors.address}
                      />
                    </Grid>
                  </Grid>
                </Form>
              </Grid>
              <Grid item sm={12} md={6}>
                <Box>
                  {!!editMode ? (
                    <>
                      {(!localizedRole || huJob) && (
                        <Field
                          label={t('jobPage.createRoleForm.companyProfile')}
                          name="companyProfile"
                          component={FormTextArea}
                          variant="outlined"
                          error={
                            touched.companyProfile || isSubmitting
                              ? errors.companyProfile
                              : undefined
                          }
                          helperText={errors.companyProfile}
                          sx={{ width: '100%' }}
                          onChange={(e: React.FocusEvent<any>) => {
                            handleChange(e);
                            setMobilePreviewData(prev => ({
                              ...prev,
                              companyProfile: e.target.value,
                            }));
                          }}
                        />
                      )}
                      {enJob && (
                        <Field
                          label={t('jobPage.createRoleForm.companyProfileEn')}
                          name="companyProfileEn"
                          component={FormTextArea}
                          variant="outlined"
                          error={
                            touched.companyProfileEn || isSubmitting
                              ? errors.companyProfileEn
                              : undefined
                          }
                          helperText={errors.companyProfileEn}
                          sx={{ width: '100%' }}
                          onChange={(e: React.FocusEvent<any>) => {
                            handleChange(e);
                            setMobilePreviewData(prev => ({
                              ...prev,
                              localizedData: {
                                ...prev.localizedData,
                                companyProfile: e.target.value,
                              },
                            }));
                          }}
                        />
                      )}
                      {(!localizedRole || huJob) && (
                        <Field
                          label={t('jobPage.createRoleForm.jobDescription')}
                          name="description"
                          component={FormTextArea}
                          variant="outlined"
                          error={
                            touched.description || isSubmitting ? errors.description : undefined
                          }
                          helperText={errors.description}
                          sx={{ width: '100%' }}
                          onChange={(e: React.FocusEvent<any>) => {
                            handleChange(e);
                            setMobilePreviewData(prev => ({
                              ...prev,
                              description: e.target.value,
                            }));
                          }}
                        />
                      )}
                      {enJob && (
                        <Field
                          label={t('jobPage.createRoleForm.jobDescriptionEn')}
                          name="descriptionEn"
                          component={FormTextArea}
                          variant="outlined"
                          error={
                            touched.descriptionEn || isSubmitting ? errors.descriptionEn : undefined
                          }
                          helperText={errors.descriptionEn}
                          sx={{ width: '100%' }}
                          onChange={(e: React.FocusEvent<any>) => {
                            handleChange(e);
                            setMobilePreviewData(prev => ({
                              ...prev,
                              localizedData: {
                                ...prev.localizedData,
                                description: e.target.value,
                              },
                            }));
                          }}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {selectedLanguages.hu && (
                        <Field
                          label={t('jobPage.createRoleForm.companyProfile')}
                          name="companyProfile"
                          component={FormTextArea}
                          variant="outlined"
                          error={
                            touched.companyProfile || isSubmitting
                              ? errors.companyProfile
                              : undefined
                          }
                          helperText={errors.companyProfile}
                          sx={{ width: '100%' }}
                          onChange={(e: React.FocusEvent<any>) => {
                            handleChange(e);
                            setMobilePreviewData(prev => ({
                              ...prev,
                              companyProfile: e.target.value,
                            }));
                          }}
                        />
                      )}
                      {selectedLanguages.en && (
                        <Field
                          label={t('jobPage.createRoleForm.companyProfileEn')}
                          name="companyProfileEn"
                          component={FormTextArea}
                          variant="outlined"
                          error={
                            touched.companyProfileEn || isSubmitting
                              ? errors.companyProfileEn
                              : undefined
                          }
                          helperText={errors.companyProfileEn}
                          sx={{ width: '100%' }}
                          onChange={(e: React.FocusEvent<any>) => {
                            handleChange(e);
                            setMobilePreviewData(prev => ({
                              ...prev,
                              localizedData: {
                                ...prev.localizedData,
                                companyProfile: e.target.value,
                              },
                            }));
                          }}
                        />
                      )}
                      {selectedLanguages.hu && (
                        <Field
                          label={t('jobPage.createRoleForm.jobDescription')}
                          name="description"
                          component={FormTextArea}
                          variant="outlined"
                          error={
                            touched.description || isSubmitting ? errors.description : undefined
                          }
                          helperText={errors.description}
                          sx={{ width: '100%' }}
                          onChange={(e: React.FocusEvent<any>) => {
                            handleChange(e);
                            setMobilePreviewData(prev => ({
                              ...prev,
                              description: e.target.value,
                            }));
                          }}
                        />
                      )}
                      {selectedLanguages.en && (
                        <Field
                          label={t('jobPage.createRoleForm.jobDescriptionEn')}
                          name="descriptionEn"
                          component={FormTextArea}
                          variant="outlined"
                          error={
                            touched.descriptionEn || isSubmitting ? errors.descriptionEn : undefined
                          }
                          helperText={errors.descriptionEn}
                          sx={{ width: '100%' }}
                          onChange={(e: React.FocusEvent<any>) => {
                            handleChange(e);
                            setMobilePreviewData(prev => ({
                              ...prev,
                              localizedData: {
                                ...prev.localizedData,
                                description: e.target.value,
                              },
                            }));
                          }}
                        />
                      )}
                    </>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  {!!onCancel && (
                    <Box mr={3}>
                      <Button onClick={onCancel}>
                        <Trans>generic.cancel</Trans>
                      </Button>
                    </Box>
                  )}
                  <Box ml={3}>
                    <Button
                      variant={BUTTON_VARIANT.GRADIENT}
                      color={BUTTON_COLOR.DARK}
                      type="submit"
                      onClick={() => {
                        handleSubmit(values, validateForm);
                        Object.keys(values).forEach(field => {
                          setFieldTouched(field, true);
                        });
                      }}>
                      <Trans>jobPage.createRoleForm.submitButtonLabel</Trans>
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          );
        }}
      </Formik>
    </Box>
  );
};
